import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

const AdminFinancialScenarios = () => {
  const navigate = useNavigate()
  const [scenarios, setScenarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingScenario, setEditingScenario] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    topics_text: '',
    grade: 4,
    is_published: true,
    is_free: true,
    order_number: 0,
    estimated_duration_minutes: 15
  })

  useEffect(() => {
    fetchScenarios()
  }, [])

  const fetchScenarios = async () => {
    try {
      setLoading(true)
      
      // Get token from Supabase session
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      if (!token) {
        setError('Nie ste prihlásený')
        setLoading(false)
        return
      }
      
      const response = await fetch(`${API_URL}/api/admin/financial-scenarios`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch scenarios')
      }
      
      const data = await response.json()
      setScenarios(data)
      setError('')
    } catch (err) {
      console.error('Error fetching scenarios:', err)
      setError('Nepodarilo sa načítať scenáre')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingScenario(null)
    setFormData({
      title: '',
      slug: '',
      description: '',
      topics_text: '',
      grade: 4,
      is_published: true,
      is_free: true,
      order_number: 0,
      estimated_duration_minutes: 15
    })
    setShowModal(true)
  }

  const handleEdit = (scenario) => {
    setEditingScenario(scenario)
    setFormData({
      title: scenario.title,
      slug: scenario.slug,
      description: scenario.description || '',
      topics_text: scenario.topics_text || '',
      grade: scenario.grade,
      is_published: scenario.is_published,
      is_free: scenario.is_free,
      order_number: scenario.order_number,
      estimated_duration_minutes: scenario.estimated_duration_minutes
    })
    setShowModal(true)
  }

  const handleSave = async () => {
    try {
      // Get token from Supabase session
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      if (!token) {
        setError('Nie ste prihlásený')
        return
      }
      
      const url = editingScenario
        ? `${API_URL}/api/admin/financial-scenarios/${editingScenario.id}`
        : `${API_URL}/api/admin/financial-scenarios`
      
      const response = await fetch(url, {
        method: editingScenario ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to save scenario')
      }
      
      await fetchScenarios()
      setShowModal(false)
      setError('')
    } catch (err) {
      console.error('Error saving scenario:', err)
      setError(err.message)
    }
  }

  const handleDelete = async (scenarioId) => {
    try {
      // Get token from Supabase session
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      if (!token) {
        setError('Nie ste prihlásený')
        return
      }
      
      const response = await fetch(`${API_URL}/api/admin/financial-scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete scenario')
      }
      
      await fetchScenarios()
      setDeleteConfirm(null)
      setError('')
    } catch (err) {
      console.error('Error deleting scenario:', err)
      setError('Nepodarilo sa vymazať scenár')
    }
  }

  const handleTogglePublish = async (scenarioId, currentStatus) => {
    try {
      // Get token from Supabase session
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      
      if (!token) {
        setError('Nie ste prihlásený')
        return
      }
      
      const response = await fetch(`${API_URL}/api/admin/financial-scenarios/${scenarioId}/publish?publish=${!currentStatus}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to toggle publish status')
      }
      
      await fetchScenarios()
    } catch (err) {
      console.error('Error toggling publish:', err)
      setError('Nepodarilo sa zmeniť stav publikovania')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Načítavam...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Finančná gramotnosť - Scenáre</h1>
              <p className="text-gray-600 mt-2">Správa metadát scenárov</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                ← Späť
              </button>
              <button
                onClick={handleCreate}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                + Pridať scenár
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Scenarios List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Názov
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ročník
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Poradie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trvanie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akcie
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scenarios.map((scenario) => (
                <tr key={scenario.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{scenario.title}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">{scenario.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                      {scenario.slug}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{scenario.grade}. ročník</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{scenario.order_number}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{scenario.estimated_duration_minutes} min</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex text-xs px-2 py-1 rounded-full ${
                        scenario.is_published 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {scenario.is_published ? '✓ Publikovaný' : '✗ Nepublikovaný'}
                      </span>
                      <span className={`inline-flex text-xs px-2 py-1 rounded-full ${
                        scenario.is_free 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {scenario.is_free ? '🆓 Zdarma' : '💰 Platený'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleTogglePublish(scenario.id, scenario.is_published)}
                        className={`px-3 py-1 rounded ${
                          scenario.is_published 
                            ? 'bg-gray-200 hover:bg-gray-300 text-gray-700' 
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {scenario.is_published ? 'Skryť' : 'Publikovať'}
                      </button>
                      <button
                        onClick={() => handleEdit(scenario)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Upraviť
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(scenario)}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Vymazať
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {scenarios.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">Žiadne scenáre</p>
              <button
                onClick={handleCreate}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Pridať prvý scenár
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingScenario ? 'Upraviť scenár' : 'Nový scenár'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Názov *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="napr. Školský bufet"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slug (URL) *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="school-buffet"
                  />
                  <p className="text-xs text-gray-500 mt-1">Použite iba malé písmená, číslice a pomlčky</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Popis
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Krátky popis scenára..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Témy
                  </label>
                  <textarea
                    value={formData.topics_text}
                    onChange={(e) => setFormData({...formData, topics_text: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="2"
                    placeholder="Rozpočet, plánovanie, mesačné náklady..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ročník *
                    </label>
                    <select
                      value={formData.grade}
                      onChange={(e) => setFormData({...formData, grade: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {[1,2,3,4,5,6,7,8,9].map(g => (
                        <option key={g} value={g}>{g}. ročník</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Poradie
                    </label>
                    <input
                      type="number"
                      value={formData.order_number}
                      onChange={(e) => setFormData({...formData, order_number: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Odhadovaná doba trvania (minúty)
                  </label>
                  <input
                    type="number"
                    value={formData.estimated_duration_minutes}
                    onChange={(e) => setFormData({...formData, estimated_duration_minutes: parseInt(e.target.value) || 15})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Publikovaný</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_free}
                      onChange={(e) => setFormData({...formData, is_free: e.target.checked})}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Zdarma (bez školy)</span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="mt-6 flex gap-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Zrušiť
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingScenario ? 'Uložiť zmeny' : 'Vytvoriť scenár'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Potvrdenie vymazania</h3>
            <p className="text-gray-600 mb-6">
              Naozaj chcete vymazať scenár <strong>{deleteConfirm.title}</strong>?
            </p>
            <p className="text-sm text-orange-600 mb-6">
              ⚠️ Toto vymaže len metadata. Samotná stránka scenára zostane.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Zrušiť
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Vymazať
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminFinancialScenarios
